import { Badge } from '@/components/ui/badge'
import { Check } from 'lucide-react'
import { monthlyPriceOf } from '@/features/subscriptions/planOrdering'

const MONTHS_PER_YEAR = 12

/** Whole-percent saving of the yearly price against twelve monthly ones. */
function yearlySavingPercent(plan) {
  const monthly = monthlyPriceOf(plan)
  const yearly = parseFloat(plan?.price_yearly)
  if (!Number.isFinite(yearly) || monthly <= 0) return 0
  return Math.round((1 - yearly / (monthly * MONTHS_PER_YEAR)) * 100)
}

/**
 * Compact plan picker: one row per plan, plus a monthly/yearly toggle.
 *
 * Deliberately smaller than PlanComparisonTable - this renders inside a dialog
 * over whatever the user was doing, so it shows the headline limits only and
 * leaves the full comparison to the subscription settings page.
 *
 * @param {Object} props
 * @param {Array<Object>} props.plans - plans to offer
 * @param {string|null} props.selectedPlanId - currently selected plan
 * @param {(planId: string) => void} props.onSelectPlan
 * @param {'monthly'|'yearly'} props.billingCycle
 * @param {(cycle: 'monthly'|'yearly') => void} props.onChangeBillingCycle
 * @param {boolean} [props.disabled] - lock the controls while a change is in flight
 * @returns {JSX.Element}
 */
export default function PlanUpgradeOptions({
  plans,
  selectedPlanId,
  onSelectPlan,
  billingCycle,
  onChangeBillingCycle,
  disabled = false,
}) {
  const isYearly = billingCycle === 'yearly'

  return (
    <div className="space-y-3">
      {/* Billing cycle */}
      <div className="flex items-center gap-2" role="radiogroup" aria-label="Billing cycle">
        {['monthly', 'yearly'].map((cycle) => (
          <button
            key={cycle}
            type="button"
            role="radio"
            aria-checked={billingCycle === cycle}
            disabled={disabled}
            onClick={() => onChangeBillingCycle(cycle)}
            className={`flex-1 rounded-control border px-3 py-1.5 text-sm capitalize transition-colors disabled:opacity-50 ${
              billingCycle === cycle
                ? 'border-brand bg-brand-subtle font-medium text-brand-ink'
                : 'border-line hover:border-line-2'
            }`}
          >
            {cycle}
          </button>
        ))}
      </div>

      {/* Plans */}
      <div className="space-y-2">
        {plans.map((plan) => {
          const isSelected = plan.id === selectedPlanId
          const saving = yearlySavingPercent(plan)
          const price = isYearly ? parseFloat(plan.price_yearly) : monthlyPriceOf(plan)

          return (
            <button
              key={plan.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => onSelectPlan(plan.id)}
              className={`flex w-full items-center justify-between rounded-control border-2 p-3 text-left transition-colors disabled:opacity-50 ${
                isSelected ? 'border-brand bg-brand-subtle' : 'border-line hover:border-line-2'
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{plan.display_name}</span>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-brand" />}
                  {isYearly && saving > 0 && (
                    <Badge variant="success">Save {saving}%</Badge>
                  )}
                </div>
                {plan.description && (
                  <p className="mt-0.5 truncate text-sm text-ink-2">
                    {plan.description}
                  </p>
                )}
              </div>
              <div className="ml-3 shrink-0 text-right">
                <p className="text-lg font-bold">
                  ${Number.isFinite(price) ? price.toFixed(0) : '—'}
                </p>
                <p className="text-xs text-ink-2">
                  {isYearly ? '/year' : '/month'}
                </p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
